import { reviewFreshness, type ReviewFreshness } from './reviewFreshness';
import type { ReviewedItem, ReviewItem } from '../api';

export interface ReviewRow {
  id: string;
  name: string;
  reviewer: string;
  reviewedOn: string;
  /** '' when the item belongs to no group. */
  group: string;
  canReview: boolean;
  /** Computed once here rather than per template binding (the table reads it 3+ times a row). */
  freshness: ReviewFreshness;
}

// The list endpoint's reviewItem schema declares reviewedItem as a bare object (only the detail
// endpoint's reviewItemAWS/GitHub/CoreMetadata narrow it), though the backend always fills
// name/reference here too. Cast at this one boundary, flattening into a row shape the table can
// sort and filter on directly rather than via per-column casts in the template.
export function toReviewRow(item: ReviewItem): ReviewRow {
  const reviewedItem = item.reviewedItem as ReviewedItem | undefined;
  return {
    id: item.id,
    name: reviewedItem?.name ?? item.id,
    reviewer: item.reviewer.name,
    reviewedOn: item.reviewedOn,
    group: item.group ?? '',
    canReview: item.canReview,
    freshness: reviewFreshness(item.reviewedOn, item.reviewType),
  };
}

/** A group's rows rolled up to its worst freshness band, plus how many rows sit in each stale band. */
export function groupFreshness(rows: ReviewRow[]): { worst: ReviewFreshness; overdue: number; due: number } {
  const overdue = rows.filter((row) => row.freshness.color === 'error').length;
  const due = rows.filter((row) => row.freshness.color === 'warning').length;
  const worst = rows.reduce((a, b) => (b.freshness.days > a.freshness.days ? b : a)).freshness;
  return { worst, overdue, due };
}
